import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

function createGradient(color1, color2) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

// SETUP COLORS - ERC-721 Premium Asset Platform Design
const PRIMARY = {
  lighter: '#fef7e0',
  light: '#fbbf24',
  main: '#f59e0b',
  dark: '#d97706',
  darker: '#92400e',
  contrastText: '#fff',
};

const SECONDARY = {
  lighter: '#f3e8ff',
  light: '#c084fc',
  main: '#8b5cf6',
  dark: '#7c3aed',
  darker: '#5b21b6',
  contrastText: '#fff',
};

const INFO = {
  lighter: '#e0f2fe',
  light: '#4dd0e1',
  main: '#00acc1',
  dark: '#00838f',
  darker: '#006064',
  contrastText: '#fff',
};

const SUCCESS = {
  lighter: '#ecfdf5',
  light: '#6ee7b7',
  main: '#10b981',
  dark: '#059669',
  darker: '#047857',
  contrastText: '#fff',
};

// ERC-721 Specific Colors
const PREMIUM = {
  lighter: '#fffbeb',
  light: '#fed7aa',
  main: '#ea580c',
  dark: '#c2410c',
  darker: '#9a3412',
  contrastText: '#fff',
};

const LUXURY = {
  lighter: '#fdf4ff',
  light: '#e879f9',
  main: '#d946ef',
  dark: '#c026d3',
  darker: '#a21caf',
  contrastText: '#fff',
};

const RARE = {
  lighter: '#f0f9ff',
  light: '#7dd3fc',
  main: '#0ea5e9',
  dark: '#0284c7',
  darker: '#0369a1',
  contrastText: '#fff',
};

const WARNING = {
  lighter: '#fef3c7',
  light: '#fcd34d',
  main: '#f59e0b',
  dark: '#d97706',
  darker: '#b45309',
  contrastText: '#fff',
};

const ERROR = {
  lighter: '#fee2e2',
  light: '#fca5a5',
  main: '#ef4444',
  dark: '#dc2626',
  darker: '#b91c1c',
  contrastText: '#fff',
};

const GREY = {
  0: '#FFFFFF',
  100: '#f9fafb',
  200: '#f3f4f6',
  300: '#e5e7eb',
  400: '#d1d5db',
  500: '#9ca3af',
  600: '#6b7280',
  700: '#4b5563',
  800: '#374151',
  900: '#1f2937',
  500_8: alpha('#9ca3af', 0.08),
  500_12: alpha('#9ca3af', 0.12),
  500_16: alpha('#9ca3af', 0.16),
  500_24: alpha('#9ca3af', 0.24),
  500_32: alpha('#9ca3af', 0.32),
  500_48: alpha('#9ca3af', 0.48),
  500_56: alpha('#9ca3af', 0.56),
  500_80: alpha('#9ca3af', 0.8),
};

const GRADIENTS = {
  primary: createGradient(PRIMARY.light, PRIMARY.main),
  secondary: createGradient(SECONDARY.light, SECONDARY.main),
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main),
  // ERC-721 Premium Gradients
  brand: `linear-gradient(135deg, ${PRIMARY.main}, ${SECONDARY.main})`,
  brandReverse: `linear-gradient(135deg, ${SECONDARY.main}, ${PRIMARY.main})`,
  subtle: `linear-gradient(135deg, ${PRIMARY.lighter}, ${SECONDARY.lighter})`,
  // ERC-721 Specific Gradients
  premium: `linear-gradient(135deg, ${PREMIUM.main}, ${PRIMARY.main})`,
  luxury: `linear-gradient(135deg, ${LUXURY.main}, ${SECONDARY.main})`,
  rare: `linear-gradient(135deg, ${RARE.main}, ${INFO.main})`,
  golden: `linear-gradient(135deg, #fbbf24, #f59e0b, #d97706)`,
  royal: `linear-gradient(135deg, #8b5cf6, #7c3aed, #5b21b6)`,
};

const CHART_COLORS = {
  violet: ['#8b5cf6', '#c084fc', '#d8b4fe', '#f3e8ff'],
  blue: ['#00acc1', '#4dd0e1', '#7dd3fc', '#e0f2fe'],
  green: ['#10b981', '#6ee7b7', '#86efac', '#ecfdf5'],
  yellow: ['#f59e0b', '#fbbf24', '#fed7aa', '#fef7e0'],
  red: ['#ef4444', '#f87171', '#fca5a5', '#fee2e2'],
  // ERC-721 Premium Chart Colors
  gold: ['#f59e0b', '#fbbf24', '#fed7aa', '#fffbeb'],
  purple: ['#8b5cf6', '#c084fc', '#e879f9', '#fdf4ff'],
  premium: ['#ea580c', '#fed7aa', '#fffbeb', '#fff7ed'],
  luxury: ['#d946ef', '#e879f9', '#f0abfc', '#fdf4ff'],
  rare: ['#0ea5e9', '#7dd3fc', '#bae6fd', '#f0f9ff'],
};

const COMMON = {
  common: { black: '#1f2937', white: '#fff' },
  primary: { ...PRIMARY, contrastText: '#fff' },
  secondary: { ...SECONDARY, contrastText: '#fff' },
  info: { ...INFO, contrastText: '#fff' },
  success: { ...SUCCESS, contrastText: '#fff' },
  warning: { ...WARNING, contrastText: '#fff' },
  error: { ...ERROR, contrastText: '#fff' },
  // ERC-721 Specific Colors
  premium: { ...PREMIUM, contrastText: '#fff' },
  luxury: { ...LUXURY, contrastText: '#fff' },
  rare: { ...RARE, contrastText: '#fff' },
  grey: GREY,
  gradients: GRADIENTS,
  chart: CHART_COLORS,
  divider: GREY[500_24],
  action: {
    hover: GREY[500_8],
    selected: GREY[500_16],
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: GREY[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

const palette = {
  light: {
    ...COMMON,
    mode: 'light',
    text: { 
      primary: GREY[800], 
      secondary: GREY[600], 
      disabled: GREY[500] 
    },
    background: { 
      paper: '#fff', 
      default: '#fff', 
      neutral: GREY[100],
      // ERC-721 Premium Brand Background
      brand: `linear-gradient(135deg, ${alpha(PRIMARY.main, 0.05)}, ${alpha(SECONDARY.main, 0.05)})`,
      premium: `linear-gradient(135deg, ${alpha(PREMIUM.main, 0.03)}, ${alpha(PRIMARY.main, 0.03)})`,
      luxury: `linear-gradient(135deg, ${alpha(LUXURY.main, 0.03)}, ${alpha(SECONDARY.main, 0.03)})`,
      rare: `linear-gradient(135deg, ${alpha(RARE.main, 0.03)}, ${alpha(INFO.main, 0.03)})`,
    },
    action: { active: GREY[600], ...COMMON.action },
  },
  dark: {
    ...COMMON,
    mode: 'dark',
    text: { 
      primary: '#fff', 
      secondary: GREY[400], 
      disabled: GREY[600] 
    },
    background: { 
      paper: GREY[800], 
      default: GREY[900], 
      neutral: GREY[500_16],
      // ERC-721 Premium Dark Background
      brand: `linear-gradient(135deg, ${alpha(PRIMARY.dark, 0.1)}, ${alpha(SECONDARY.dark, 0.1)})`,
      premium: `linear-gradient(135deg, ${alpha(PREMIUM.dark, 0.08)}, ${alpha(PRIMARY.dark, 0.08)})`,
      luxury: `linear-gradient(135deg, ${alpha(LUXURY.dark, 0.08)}, ${alpha(SECONDARY.dark, 0.08)})`,
      rare: `linear-gradient(135deg, ${alpha(RARE.dark, 0.08)}, ${alpha(INFO.dark, 0.08)})`,
    },
    action: { active: GREY[500], ...COMMON.action },
  },
};

export default palette;